<template>
  <lf-pill
    v-if="dispositionInfo"
    :color-index="dispositionInfo.currentColorOfStatus"
    class="max-w-fit"
  >
    <p v-tooltipable class="truncate font-semibold uppercase">
      {{ dispositionInfo.callDisposition }}
    </p>
  </lf-pill>

  <span
    v-if="dealNoteStage"
    v-tooltip="{
      content: dealNoteStage.name,
      placement: 'left',
      onShow: () => (dealNoteStage?.name?.length ?? 0) > 30,
    }"
    class="max-w-3/4 min-w-max overflow-hidden rounded-full px-2-5 py-1 text-xxs text-ellipsis whitespace-nowrap uppercase"
    :style="getPillStyle({ color: COLORS[dealNoteStage.color] })"
  >
    {{ dealNoteStage.name }}
  </span>

  <div v-if="isTaskLog(log)" class="flex items-center space-x-2">
    <task-due-date
      v-if="log.data.due_at"
      :date="log.data.due_at"
      @update="updateDueDate"
      @click.stop
    />
    <lf-user-selection
      :key="log.data?.assignee?.id"
      v-tooltip="{
        content: () =>
          log.data.assignee
            ? $t('ACTIVITY.TASK.ASSIGNEE', {
                name: formatUserFullName(log.data.assignee),
              })
            : $t('COMMON.UNASSIGNED'),
        onShow: () => log.data.assignee,
      }"
      name="assignee"
      :value="log.data?.assignee"
      :initial-user-selection="log.data?.assignee"
      disabled
      avatar-only-trigger-button
      value-type="user"
      @click.stop
    />
    <task-priority-dropdown
      v-if="log.data?.priority_level"
      v-tooltip="log.data.priority_level"
      :priority-level="log.data.priority_level"
      :show-value="false"
      disabled
      @click.stop
    />
    <div class="max-w-30 min-w-30">
      <task-status-dropdown
        v-if="log.data?.status"
        :task-status="log.data.status"
        :is-loading="isPending"
        @change="handleStatusChange"
        @click.stop
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { FullNotableType } from "@/enums/dealActivity";
import { getPillStyle } from "@/helpers/UI";
import { isCallLog, isNoteLog, isTaskLog } from "@/helpers/communication";
import { COLORS, STATUS_COLOR_CODES } from "@/helpers/constants";
import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { WORKFLOW_TO_APP_STATUS } from "@/helpers/constants/workflow";
import { formatUserFullName } from "@/helpers/formatting";
import { useUpdateTaskMutation } from "@/hooks/queries/useInbox";

import TaskDueDate from "@/components/communicationLogs/forms/TaskDueDate.vue";
import TaskPriorityDropdown from "@/components/communicationLogs/forms/TaskPriorityDropdown.vue";
import TaskStatusDropdown from "@/components/communicationLogs/forms/TaskStatusDropdown.vue";
import LfUserSelection from "@/components/ui/inputs/LfUserSelection.vue";
import LfPill from "@/components/ui/text/LfPill.vue";

import type { TaskStatuses } from "@/enums/communicationLogs";
import type { ApplicationStatus } from "@/models/applications";
import type { CommunicationLogBase } from "@/models/communicationLogs";

const { log } = defineProps<{ log: CommunicationLogBase }>();

const { t } = useI18n();
const { mutateAsync: updateTaskMutation, isPending } = useUpdateTaskMutation();

const dispositionInfo = computed(() => {
  if ((!isCallLog(log) && !isNoteLog(log)) || !log.data) {
    return null;
  }

  if (isNoteLog(log) && log.data?.notable_type !== FullNotableType.call) {
    return null;
  }

  return {
    currentColorOfStatus:
      COLORS_BY_DISPOSITION_NOTE_STATUS[
        log.data
          .call_disposition as keyof typeof COLORS_BY_DISPOSITION_NOTE_STATUS
      ],
    callDisposition:
      log.data.call_disposition?.replace(/_/g, " ") ??
      t("ACTIVITY.SELECT_CALL_DISPOSITION"),
  };
});

const dealNoteStage = computed(() => {
  if (
    !isNoteLog(log) ||
    log.data.notable_type !== FullNotableType.application
  ) {
    return null;
  }
  const stageType = log.data?.stage_name;
  // stage_name may not exactly match but we can fuzzy match
  const normalizedStageType = stageType
    ? Object.keys(WORKFLOW_TO_APP_STATUS).find((key) => {
        const normalizedKey = key.toLowerCase().replace(/\s+/g, "");
        const normalizedStage = stageType.toLowerCase().replace(/\s+/g, "");
        return (
          normalizedKey.includes(normalizedStage) ||
          normalizedStage.includes(normalizedKey)
        );
      })
    : undefined;

  const statusCode =
    normalizedStageType === undefined
      ? undefined
      : WORKFLOW_TO_APP_STATUS[
          normalizedStageType as keyof typeof WORKFLOW_TO_APP_STATUS
        ];
  return {
    color:
      stageType === undefined || statusCode === undefined
        ? 0
        : STATUS_COLOR_CODES[statusCode as ApplicationStatus],
    name: stageType ?? "",
  };
});

const handleStatusChange = async (status: TaskStatuses) => {
  await updateTaskMutation({ id: log.data.id, status });
};

const updateDueDate = async (due_at: string) => {
  if (!log.data.id) {
    return;
  }
  await updateTaskMutation({
    id: log.data.id,
    due_at: new Date(due_at).toISOString(),
  });
};
</script>
